import networkRequest from '../utils/networkRequest'

/**
 * 精选产品（Featured Products）数据层。
 *
 * 与 roadlinkglb 的 src/api/home.js 复用同一套后端接口（zjs/mini/product/*），
 * 但做了两处适配，使其符合 roadlinkglobal（国际站）的约定：
 *   1. 网络层改用本项目的 networkRequest（dev 走 /api 代理，production 走 api.mini.roadlinkglb.com；URL 由 networkRequest 规范化拼接，确保 baseURL 与路径间有且仅有一个 /）；
 *   2. 字段归一化为英文命名，且「类型 / 分类」标签通过 PRODUCT_TYPE_EN 映射为英文展示。
 */

// 产品类型中英文映射：国际站统一用英文展示「类型 / 分类」标签。
// 键为后端返回的中文类型名；未命中时原样返回，避免空白。
// 新增类型翻译时，直接在此对象追加键值即可。
export const PRODUCT_TYPE_EN = {
  '金融服务': 'Financial Services',
  '财税服务': 'Tax & Accounting',
  '法律合规': 'Legal & Compliance',
  '市场拓展': 'Market Expansion',
  '供应链': 'Supply Chain',
  '咨询服务': 'Advisory',
  '知识产权': 'Intellectual Property',
  '海外公司': 'Overseas Company',
  '移民签证': 'Immigration & Visa',
  '人力资源': 'Human Resources',
  '品牌营销': 'Brand & Marketing',
  'IT 服务': 'IT Services',
  // 以下为实测接口返回的真实类型（typeNameEn 目前为空，先用字典兜底英文展示）
  '数码': 'Electronics',
  '智能手表': 'Smart Watch',
  '新型智能手表': 'New Smart Watch'
}

export function localizeTypeName (name) {
  if (!name) return ''
  return PRODUCT_TYPE_EN[name] || name
}

// 后端 code !== 0 时抛错，让视图层统一走错误提示（与 glb 的 post 行为一致）
function unwrap (body) {
  if (body && typeof body.code === 'number' && body.code !== 0) {
    throw new Error(body.msg || body.message || 'Request failed')
  }
  return body
}

// 将后端产品对象归一化为英文命名字段
// 优先取后端 *En 英文态字段；其次走中英映射字典；兜底用中文原值
function pickEn (enVal, zhVal) {
  if (enVal) return enVal
  return localizeTypeName(zhVal)
}

function normalizeProduct (p) {
  return {
    id: p.id,
    name: pickEn(p.nameEn, p.name),
    cover: p.cover,
    summary: pickEn(p.summaryEn, p.summary),
    content: pickEn(p.contentEn, p.content),
    category: pickEn(p.typeNameEn, p.typeName),
    subCategory: pickEn(p.secondTypeNameEn, p.secondTypeName),
    thirdCategory: pickEn(p.thirdTypeNameEn, p.thirdTypeName),
    sourceCountry: pickEn(p.sourcePlaceEn, p.sourcePlace),
    // 保留原始一/二/三级类型 id，供列表页按层级筛选 / 回显
    secondTypeId: p.secondTypeId,
    thirdTypeId: p.thirdTypeId
  }
}

// 首页精选产品：product/list（data 字段即产品数据；兼容直接返回 rows 的情况）
export async function fetchProducts () {
  const data = unwrap(await networkRequest.post('zjs/mini/product/list', { pageNum: 1, pageSize: 20 }))
  // 优先取 data（可能为字符串/对象），兜底用 rows（与小程序 queryProductList 逻辑一致）
  let list = []
  const raw = data.data
  if (typeof raw === 'string') {
    try { list = JSON.parse(raw) } catch (e) { list = [] }
  } else if (Array.isArray(raw)) {
    list = raw
  } else if (raw && typeof raw === 'object') {
    list = raw.list || raw.records || raw.rows || raw.content || raw.data || raw.products || []
  }
  if (!list.length) list = data.rows || []
  return list.map(normalizeProduct)
}

// 产品详情：product/detail，参数为 id，返回 data
export async function fetchProductDetail (id) {
  const data = unwrap(await networkRequest.post('zjs/mini/product/detail', { id }))
  const p = data.data || {}
  return normalizeProduct(p)
}

// ============ 精选产品（按类型分类展示 / 分页） ============

// 一级产品类型：product/type/firstLevel（POST，支持 pageNum/pageSize 分页）
export async function fetchProductFirstTypes ({ pageNum = 1, pageSize = 12 } = {}) {
  const data = unwrap(await networkRequest.post('zjs/mini/product/type/firstLevel', { pageNum, pageSize }))
  return {
    rows: (data.rows || []).map(t => ({
      id: t.id,
      name: pickEn(t.nameEn, t.name),
      level: t.level,
      parentId: t.parentId,
      parentName: pickEn(t.parentNameEn, t.parentName),
      sort: t.sort
    })),
    total: data.total || 0
  }
}

// 递归归一化类型节点，保留嵌套 children（二级节点携带三级 children，供三级联动展开）
function mapTypeNode (c) {
  return {
    id: c.id,
    name: pickEn(c.nameEn, c.name),
    level: c.level,
    parentId: c.parentId,
    children: Array.isArray(c.children) ? c.children.map(mapTypeNode) : []
  }
}

// 产品类型树：product/type/tree（POST，参数为一级类型 id，data.children 为二级/三级）
export async function fetchProductTypeTree (firstLevelId) {
  const data = unwrap(await networkRequest.post('zjs/mini/product/type/tree', { id: firstLevelId }))
  const node = data.data || {}
  return {
    id: node.id,
    name: pickEn(node.nameEn, node.name),
    children: Array.isArray(node.children) ? node.children.map(mapTypeNode) : []
  }
}

// 产品列表（按类型）：product/listByType（POST，typeId/secondTypeId/thirdTypeId 均可选，留空不按该层级过滤）
export async function fetchProductsByType ({ typeId, secondTypeId, thirdTypeId, pageNum = 1, pageSize = 12 } = {}) {
  const data = unwrap(await networkRequest.post('zjs/mini/product/listByType', {
    typeId,
    secondTypeId: secondTypeId || undefined,
    thirdTypeId: thirdTypeId || undefined,
    pageNum,
    pageSize
  }))
  return {
    rows: (data.rows || []).map(normalizeProduct),
    total: data.total || 0
  }
}
